import { Box, Typography } from '@mui/material';

import { useScreenInfo } from 'src/hooks/common';
import { SxProp } from 'src/types/mui';

interface AuthRoleCardProps {
  title: string;
  text: string;
  dataElement: string;
  icon: React.ReactNode;
  signInButton?: React.ReactNode;
  signUpButton?: React.ReactNode;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
}

export const AuthRoleCard: React.FC<AuthRoleCardProps> = ({
  title,
  text,
  icon,
  signInButton,
  signUpButton,
  dataElement,
}) => {
  const { isMobile } = useScreenInfo();
  const styles = isMobile ? cardStylesMobile : cardStyles;

  return (
    <Box
      data-e2e-element={dataElement}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={styles.container}
    >
      <Box sx={styles.body}>
        <Box display="flex" sx={styles.icon}>
          {icon}
        </Box>

        <Box sx={styles.textBox}>
          <Typography variant="subtitle1" sx={styles.title}>
            {title}
          </Typography>

          <Typography variant="body2" sx={styles.text}>
            {text}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.buttons}>
        {signUpButton}
        {signInButton}
      </Box>
    </Box>
  );
};

const cardStyles: Record<string, SxProp> = {
  container: {
    maxWidth: 190,
    height: 278,
    py: 1.5,
    mx: 1,
    display: 'flex',
    flexDirection: 'column',
    alighItems: 'center',
    transition: 'box-shadow 0.3s ease-in-out',
    borderRadius: '8px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',

    '&:hover': {
      boxShadow: '0px 0px 15px 1px rgba(0, 0, 0, 0.15)',
    },
  },

  body: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  icon: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },

  textBox: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    px: 2,
  },

  title: {
    mt: 1.5,
  },

  text: {
    mt: 2,
    textAlign: 'center',
  },

  buttons: {
    display: 'flex',
    justifyContent: 'center',
    mt: 'auto',
    gap: 1,
    whiteSpace: 'nowrap',
  },
};

const cardStylesMobile: Record<string, SxProp> = {
  container: {
    my: 1,
    display: 'flex',
    backgroundColor: '#FFFFFF',
    boxShadow:
      '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',
    borderRadius: '8px',
  },

  body: {
    mt: 2,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
  },

  icon: {
    display: 'flex',
    justifyContent: 'center',
    px: 2,
    maxWidth: 64,
    maxHeight: 64,
  },

  textBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    pr: 6,
    mb: 2,
  },

  title: {
    pt: 0,
    mt: 0,
  },

  text: {
    mt: 1,
    fontSize: '16px',
  },

  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
    px: 2,
    pb: 2,

    gap: 1,
    whiteSpace: 'nowrap',
  },
};
